import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

import { EmptyState } from "./EmptyState";
import { FileBulkToolbar } from "./FileBulkToolbar";
import { FileTable } from "./FileTable";
import { FileUploadZone } from "./FileUploadZone";
import { FolderBreadcrumb } from "./FolderBreadcrumb";
import { FolderToolbar } from "./FolderToolbar";
import { FolderTreeSidebar } from "./FolderTreeSidebar";
import { ProjectStorageSkeleton } from "./ProjectStorageSkeleton";
import { SubfolderGrid } from "./SubfolderGrid";
import { UploadProgressList } from "./UploadProgressList";
import { CreateFolderModal } from "./modals/CreateFolderModal";
import { DeleteFilesModal } from "./modals/DeleteFilesModal";
import { DeleteFolderModal } from "./modals/DeleteFolderModal";
import { FolderRenameModal } from "./modals/FolderRenameModal";
import { RenameModal } from "./modals/RenameModal";
import { useCreateFolder } from "../hooks/useCreateFolder";
import { useDeleteFiles } from "../hooks/useDeleteFiles";
import { useDeleteFolder } from "../hooks/useDeleteFolder";
import { useFileDownload } from "../hooks/useFileDownload";
import { useFolderChildren } from "../hooks/useFolderChildren";
import { useFolderCounts } from "../hooks/useFolderCounts";
import { useFolderTree } from "../hooks/useFolderTree";
import { useProjectStoragePermissions } from "../hooks/useProjectStoragePermissions";
import { useRenameFile } from "../hooks/useRenameFile";
import { useRenameFolder } from "../hooks/useRenameFolder";
import { useStorageMmCode } from "../hooks/useStorageMmCode";
import { useUploadBatch } from "../hooks/useUploadBatch";
import type { FileListItem } from "../types/project-storage.types";
import {
  buildBreadcrumbSegments,
  findFolderInTree,
  getParentFolderId,
} from "../utils/folder-tree";

export default function ProjectStoragePage() {
  const { mmCode, isProjectLoading } = useStorageMmCode();
  const { canEdit } = useProjectStoragePermissions();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    () => new Set()
  );
  const [renameTarget, setRenameTarget] = useState<FileListItem | null>(null);
  const [renameServerError, setRenameServerError] = useState<string | null>(null);
  const [deleteTargetIds, setDeleteTargetIds] = useState<string[]>([]);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderServerError, setCreateFolderServerError] = useState<string | null>(
    null
  );
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderServerError, setRenameFolderServerError] = useState<string | null>(
    null
  );

  const {
    data: tree = [],
    isLoading: isTreeLoading,
    isError: isTreeError,
  } = useFolderTree(mmCode);

  const {
    data: childrenData,
    isLoading: isChildrenLoading,
    isFetching: isChildrenFetching,
    error: childrenError,
  } = useFolderChildren(mmCode, selectedFolderId);

  const { folderCounts } = useFolderCounts(mmCode, tree, childrenData);

  const { downloadingFileId, downloadFile } = useFileDownload(
    mmCode,
    selectedFolderId
  );

  const {
    queue,
    isUploading,
    replacingItemId,
    retryingItemId,
    uploadFiles,
    replaceConflictItem,
    retryUploadItem,
    clearFinishedItems,
    canRetryUpload,
  } = useUploadBatch(mmCode, selectedFolderId);

  const { renameFileItem, isRenaming } = useRenameFile(mmCode, selectedFolderId);
  const { deleteFiles, isDeleting } = useDeleteFiles(mmCode, selectedFolderId);
  const { createFolderItem, isCreating } = useCreateFolder(mmCode, selectedFolderId);
  const { renameFolderItem, isRenaming: isRenamingFolder } = useRenameFolder(
    mmCode,
    selectedFolderId
  );
  const {
    deleteModalData,
    isDeleting: isDeletingFolder,
    isLoadingPreview,
    openDeleteModal,
    closeDeleteModal,
    confirmDeleteFolder,
  } = useDeleteFolder(mmCode);

  useEffect(() => {
    const errorCode = (childrenError as any)?.response?.data?.errorCode;
    if (errorCode === "FOLDER_NOT_FOUND") {
      setSelectedFolderId(null);
    }
  }, [childrenError]);

  useEffect(() => {
    setSelectedFileIds(new Set());
  }, [selectedFolderId]);

  const handleSelectFolder = useCallback((folderId: string) => {
    setSelectedFolderId(folderId);
  }, []);

  const handleBreadcrumbNavigate = useCallback((folderId: string | null) => {
    setSelectedFolderId(folderId);
  }, []);

  const handleToggleFile = useCallback((fileId: string) => {
    setSelectedFileIds((current) => {
      const next = new Set(current);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  const handleToggleAllFiles = useCallback(() => {
    const fileList = childrenData?.files ?? [];
    setSelectedFileIds((current) => {
      if (current.size === fileList.length) return new Set();
      return new Set(fileList.map((file) => file.id));
    });
  }, [childrenData?.files]);

  const breadcrumbSegments = useMemo(() => {
    if (!selectedFolderId) return [];

    if (childrenData?.folder.id === selectedFolderId) {
      return buildBreadcrumbSegments(tree, childrenData.folder);
    }

    const node = findFolderInTree(tree, selectedFolderId);
    if (!node) return [];

    return [{ id: node.id, label: node.displayName }];
  }, [childrenData, selectedFolderId, tree]);

  const isRootView = selectedFolderId === null;
  const isInitialLoading =
    isProjectLoading || (mmCode !== null && isTreeLoading && tree.length === 0);

  const isContentLoading =
    !!selectedFolderId &&
    (isChildrenLoading || isChildrenFetching) &&
    !childrenData;

  const subfolders = isRootView ? tree : childrenData?.subfolders ?? [];
  const files = isRootView ? [] : childrenData?.files ?? [];
  const hasSubfolders = subfolders.length > 0;
  const hasFiles = files.length > 0;
  const isFolderEmpty =
    !isRootView && !hasSubfolders && !hasFiles && !isContentLoading;
  const showUploadZone = canEdit && !isRootView && !!selectedFolderId;
  const isFileActionsBusy = isRenaming || isDeleting;
  const isFolderActionsBusy =
    isCreating || isRenamingFolder || isDeletingFolder || isLoadingPreview;

  const activeFolder = useMemo(() => {
    if (childrenData?.folder.id === selectedFolderId) {
      return childrenData.folder;
    }

    if (!selectedFolderId) return null;
    return findFolderInTree(tree, selectedFolderId);
  }, [childrenData?.folder, selectedFolderId, tree]);

  const showFolderWriteActions =
    canEdit && !!selectedFolderId && !!activeFolder && !activeFolder.isSystem;
  const canCreateFolder = canEdit && !!selectedFolderId;

  const selectedFiles = useMemo(
    () => files.filter((file) => selectedFileIds.has(file.id)),
    [files, selectedFileIds]
  );

  const handleOpenRename = useCallback((file: FileListItem) => {
    setRenameServerError(null);
    setRenameTarget(file);
  }, []);

  const handleCloseRename = useCallback(() => {
    if (isRenaming) return;
    setRenameTarget(null);
    setRenameServerError(null);
  }, [isRenaming]);

  const handleSubmitRename = useCallback(
    async (displayName: string) => {
      if (!renameTarget) return;

      const result = await renameFileItem(renameTarget.id, displayName);
      if (result.ok) {
        setRenameTarget(null);
        setRenameServerError(null);
        return;
      }

      setRenameServerError(result.errorMessage);
    },
    [renameFileItem, renameTarget]
  );

  const handleOpenDelete = useCallback((file: FileListItem) => {
    setDeleteTargetIds([file.id]);
  }, []);

  const handleOpenBulkDelete = useCallback(() => {
    setDeleteTargetIds(Array.from(selectedFileIds));
  }, [selectedFileIds]);

  const handleCloseDelete = useCallback(() => {
    if (isDeleting) return;
    setDeleteTargetIds([]);
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteTargetIds.length === 0) return;

    const result = await deleteFiles(deleteTargetIds);
    if (result.failedCount === 0) {
      setSelectedFileIds(new Set());
      setDeleteTargetIds([]);
    }
  }, [deleteFiles, deleteTargetIds]);

  const handleBulkDownload = useCallback(async () => {
    for (const file of selectedFiles) {
      await downloadFile(file);
    }
  }, [downloadFile, selectedFiles]);

  const handleOpenCreateFolder = useCallback(() => {
    setCreateFolderServerError(null);
    setCreateFolderOpen(true);
  }, []);

  const handleCloseCreateFolder = useCallback(() => {
    if (isCreating) return;
    setCreateFolderOpen(false);
    setCreateFolderServerError(null);
  }, [isCreating]);

  const handleSubmitCreateFolder = useCallback(
    async (displayName: string) => {
      const result = await createFolderItem(displayName);
      if (result.ok) {
        setCreateFolderOpen(false);
        setCreateFolderServerError(null);
        setSelectedFolderId(result.folder.id);
        return;
      }

      setCreateFolderServerError(result.errorMessage);
    },
    [createFolderItem]
  );

  const handleOpenRenameFolder = useCallback(() => {
    setRenameFolderServerError(null);
    setRenameFolderOpen(true);
  }, []);

  const handleCloseRenameFolder = useCallback(() => {
    if (isRenamingFolder) return;
    setRenameFolderOpen(false);
    setRenameFolderServerError(null);
  }, [isRenamingFolder]);

  const handleSubmitRenameFolder = useCallback(
    async (displayName: string) => {
      const result = await renameFolderItem(displayName);
      if (result.ok) {
        setRenameFolderOpen(false);
        setRenameFolderServerError(null);
        return;
      }

      setRenameFolderServerError(result.errorMessage);
    },
    [renameFolderItem]
  );

  const handleOpenDeleteFolder = useCallback(() => {
    if (!selectedFolderId || !activeFolder) return;
    openDeleteModal(selectedFolderId, activeFolder.displayName);
  }, [activeFolder, openDeleteModal, selectedFolderId]);

  const handleConfirmDeleteFolder = useCallback(async () => {
    if (!deleteModalData) return;

    const deletedFolderId = deleteModalData.folderId;
    const parentFolderId = getParentFolderId(tree, deletedFolderId);
    const result = await confirmDeleteFolder(deleteModalData);

    if (result.ok) {
      setSelectedFolderId(parentFolderId);
    }
  }, [confirmDeleteFolder, deleteModalData, tree]);

  if (isProjectLoading || mmCode === null) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flex={1}
        minHeight={320}
        bgcolor="#F9FCFF"
      >
        {isProjectLoading ? (
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Carregando projeto...</Typography>
          </Box>
        ) : (
          <Typography color="text.secondary">
            Código MM do projeto não disponível.
          </Typography>
        )}
      </Box>
    );
  }

  if (isInitialLoading) {
    return <ProjectStorageSkeleton />;
  }

  if (isTreeError) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flex={1}
        minHeight={320}
        bgcolor="#F9FCFF"
      >
        <Typography color="text.secondary">
          Não foi possível carregar o repositório de arquivos.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flex={1}
      minHeight={0}
      bgcolor="#F9FCFF"
      overflow="hidden"
    >
      <FolderTreeSidebar
        tree={tree}
        selectedFolderId={selectedFolderId}
        folderCounts={folderCounts}
        onSelectFolder={handleSelectFolder}
      />

      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        minWidth={0}
        overflow="auto"
        px={3}
        py={3}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          minHeight={32}
          mb={2}
        >
          <FolderBreadcrumb
            segments={breadcrumbSegments}
            onNavigate={handleBreadcrumbNavigate}
          />
          <FolderToolbar
            canCreate={canCreateFolder}
            canManageFolder={showFolderWriteActions}
            disabled={isFolderActionsBusy || isFileActionsBusy || isUploading}
            isLoadingDeletePreview={isLoadingPreview}
            onCreateFolder={handleOpenCreateFolder}
            onRenameFolder={handleOpenRenameFolder}
            onDeleteFolder={handleOpenDeleteFolder}
          />
        </Box>

        {isContentLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {showUploadZone && (
              <FileUploadZone
                disabled={isUploading}
                onFilesSelected={uploadFiles}
              />
            )}

            {queue.length > 0 && (
              <UploadProgressList
                items={queue}
                replacingItemId={replacingItemId}
                retryingItemId={retryingItemId}
                onReplace={canEdit ? replaceConflictItem : undefined}
                onRetry={canEdit ? retryUploadItem : undefined}
                canRetryItem={canRetryUpload}
                onDismissFinished={clearFinishedItems}
              />
            )}

            {hasSubfolders && (
              <SubfolderGrid
                folders={subfolders}
                folderCounts={folderCounts}
                onSelectFolder={handleSelectFolder}
              />
            )}

            {hasFiles && (
              <>
                <FileBulkToolbar
                  selectedCount={selectedFileIds.size}
                  disabled={isFileActionsBusy || isUploading}
                  canDelete={canEdit}
                  onDownload={handleBulkDownload}
                  onDelete={canEdit ? handleOpenBulkDelete : undefined}
                />
                <FileTable
                  files={files}
                  selectedFileIds={selectedFileIds}
                  canEdit={canEdit}
                  downloadingFileId={downloadingFileId}
                  isDeleting={isDeleting}
                  onToggleFile={handleToggleFile}
                  onToggleAll={handleToggleAllFiles}
                  onDownload={downloadFile}
                  onRename={canEdit ? handleOpenRename : undefined}
                  onDelete={canEdit ? handleOpenDelete : undefined}
                />
              </>
            )}

            {isFolderEmpty && <EmptyState />}
          </>
        )}
      </Box>

      <RenameModal
        open={renameTarget !== null}
        initialName={renameTarget?.displayName ?? ""}
        originalExtension={renameTarget?.extension ?? ""}
        isSubmitting={isRenaming}
        serverError={renameServerError}
        onClose={handleCloseRename}
        onSubmit={handleSubmitRename}
      />

      <DeleteFilesModal
        open={deleteTargetIds.length > 0}
        count={deleteTargetIds.length}
        isDeleting={isDeleting}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
      />

      <CreateFolderModal
        open={createFolderOpen}
        isSubmitting={isCreating}
        serverError={createFolderServerError}
        onClose={handleCloseCreateFolder}
        onSubmit={handleSubmitCreateFolder}
      />

      <FolderRenameModal
        open={renameFolderOpen}
        initialName={activeFolder?.displayName ?? ""}
        isSubmitting={isRenamingFolder}
        serverError={renameFolderServerError}
        onClose={handleCloseRenameFolder}
        onSubmit={handleSubmitRenameFolder}
      />

      <DeleteFolderModal
        open={deleteModalData !== null}
        folderName={deleteModalData?.displayName ?? ""}
        subfolderCount={deleteModalData?.subfolderCount ?? 0}
        fileCount={deleteModalData?.fileCount ?? 0}
        requiresConfirmation={deleteModalData?.requiresConfirmation ?? false}
        isDeleting={isDeletingFolder}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDeleteFolder}
      />
    </Box>
  );
}
